import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useDashboardProgress } from "@/lib/useDashboardProgress";
import { nextModuleId } from "@/lib/journey";
import { MemberStatusBar } from "@/components/MemberStatusBar";
import { GraduationBlock } from "@/components/GraduationBlock";
import { JourneyStrip } from "@/components/JourneyStrip";
import { ContinueBanner } from "@/components/ContinueBanner";
import { WelcomeTour } from "@/components/WelcomeTour";
import { ModuleShareDialog } from "@/components/ModuleShare";
import { StreakShareDialog } from "@/components/StreakShare";
import { WelcomeVideoDialog } from "@/components/WelcomeVideoDialog";
import { WallCelebration } from "@/components/WallCelebration";
import { VoiceJournal } from "@/components/VoiceJournal";
import { VerifyEmailBanner } from "@/components/VerifyEmailBanner";
import { LumaWelcome } from "@/components/LumaWelcome";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { DashboardQuickActions } from "@/components/dashboard/DashboardQuickActions";
import { DashboardModules } from "@/components/dashboard/DashboardModules";
import { DashboardBooks } from "@/components/dashboard/DashboardBooks";

const ENTITLED_KEYS = ["modules", "bundle", "course"];

const tourStepsFor = (hasModules, progress) => [
  {
    testid: hasModules && progress && !progress.graduated ? "continue-banner" : "dashboard-module-01",
    title: "Start with Module 01.",
    body: "This is your first step. Press Begin and Rich walks you through it — about three minutes.",
  },
  {
    testid: "journey-strip",
    title: "This is your journey.",
    body: "Eight scenes, storm to sunrise. Each tile is a module. Watch the world change as you move through them.",
  },
  {
    testid: progress?.checked_in_today ? "member-status-bar" : "streak-checkin-btn",
    title: "Ground today. Every day.",
    body: "Tap this once a day to keep your streak alive. Small and daily — that is how change sticks.",
  },
];

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const firstName = (user?.name || "Member").split(" ")[0];
  const owns = user?.entitlements || [];
  const isAdmin = user?.role === "admin";
  const hasModules = isAdmin || ENTITLED_KEYS.some((k) => owns.includes(k));
  const [shareId, setShareId] = useState(null);
  const [streakShareOpen, setStreakShareOpen] = useState(false);

  const {
    progress, setProgress, videosAvailable, welcomeOpen, closeWelcome, tourOpen, setTourOpen, finishTour,
    applyCheckin, checkin, toggleDone, toggleReminder,
  } = useDashboardProgress({ user, onShareModule: setShareId, onShareStreak: () => setStreakShareOpen(true) });

  const completed = progress?.completed_modules || [];
  const nextId = nextModuleId(completed);

  return (
    <main className="min-h-screen pt-32 md:pt-40 pb-28" data-testid="member-dashboard">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <DashboardHeader
          firstName={firstName}
          name={user?.name}
          picture={user?.picture}
          isAdmin={isAdmin}
          showTourReplay={Boolean(progress?.tour_done)}
          onReplayTour={() => setTourOpen(true)}
          onLogout={() => { logout(); navigate("/"); }}
        />

        <MemberStatusBar progress={progress} onCheckin={checkin} onToggleReminder={toggleReminder} onShareStreak={() => setStreakShareOpen(true)} />

        {progress && progress.email_verified === false && <VerifyEmailBanner email={user?.email} />}
        {progress && completed.length === 0 && !progress.graduated && <LumaWelcome firstName={firstName} />}
        {hasModules && progress && !progress.graduated && <ContinueBanner nextId={nextId} completedCount={completed.length} />}

        <JourneyStrip completed={completed} currentId={hasModules ? nextId : null} className="mb-12" />

        <div className="mb-12">
          <VoiceJournal checkedInToday={Boolean(progress?.checked_in_today)} onSaved={(c) => c && applyCheckin(c)} />
        </div>

        <DashboardQuickActions hasModules={hasModules} onOpenWorkbook={() => navigate("/workbook")} />

        <DashboardModules
          hasModules={hasModules}
          completed={completed}
          videosAvailable={videosAvailable}
          userId={user?.id}
          onToggleDone={toggleDone}
          onShare={setShareId}
        />

        {hasModules && progress?.graduated && (
          <GraduationBlock hasVideo={videosAvailable.includes("graduation")} firstName={firstName} fullName={user?.name || ""} onWall={progress.on_wall} wallDisplay={progress.wall_display} photo={user?.picture} wallShowPhoto={progress.wall_show_photo} />
        )}

        <DashboardBooks canDownload={hasModules || owns.includes("workbook")} />
      </div>

      {tourOpen && <WelcomeTour steps={tourStepsFor(hasModules, progress)} onDone={finishTour} />}
      <WelcomeVideoDialog open={welcomeOpen} onClose={closeWelcome} firstName={firstName} />
      <WallCelebration
        show={Boolean(progress?.on_wall && !progress?.wall_celebrated && !welcomeOpen && !tourOpen)}
        onDone={() => setProgress((p) => ({ ...(p || {}), wall_celebrated: true }))}
      />
      <ModuleShareDialog open={!!shareId} onOpenChange={(o) => { if (!o) setShareId(null); }} moduleId={shareId} firstName={firstName} />
      <StreakShareDialog open={streakShareOpen} onOpenChange={setStreakShareOpen} streak={progress?.streak_current || 0} firstName={firstName} />
    </main>
  );
}
